import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components'
import { Loader2 } from 'lucide-react'
import { ReactNode } from 'react'

interface PlatformConnectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Title text, e.g. "Connect Pinterest". */
  title: string
  /** Body copy under the title. */
  description: string
  /** Centered icon shown above the title (renders inside a colored circle). */
  icon: ReactNode
  /** Tailwind class(es) for the icon's circular background, e.g. `bg-[#0A66C2]`. */
  iconBgClassName: string
  /** Connect button label, e.g. "Connect LinkedIn". */
  ctaLabel: string
  /** Optional icon shown next to the connect button label (e.g. the platform logo). */
  ctaIcon?: ReactNode
  /** Tailwind class(es) for the connect button. */
  ctaClassName: string
  /** Latest error message — rendered between description and CTA when truthy. */
  errorMessage?: string | null
  /** Tailwind class(es) for the error text — defaults to the standard alert error treatment. */
  errorClassName?: string
  isConnecting: boolean
  onConnect: () => void
}

const DEFAULT_ERROR_CLASS = 'text-[color:var(--alert-error-text)] text-center text-[13px]'

/**
 * Shared "Connect &lt;Platform&gt;" dialog used by ComposerHeader for X / LinkedIn /
 * Facebook / Pinterest / TikTok. The structure (icon circle, title, description,
 * optional error, primary CTA) is identical across platforms — only the brand
 * colors and copy vary.
 */
export function PlatformConnectDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  iconBgClassName,
  ctaLabel,
  ctaIcon,
  ctaClassName,
  errorMessage,
  errorClassName = DEFAULT_ERROR_CLASS,
  isConnecting,
  onConnect,
}: PlatformConnectDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" variant="dark" withCloseButton>
        <DialogHeader>
          <div
            className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${iconBgClassName}`}
          >
            {icon}
          </div>
          <DialogTitle variant="dark" className="text-center">
            {title}
          </DialogTitle>
          <DialogDescription variant="dark" className="text-center">
            {description}
          </DialogDescription>
        </DialogHeader>
        {errorMessage && <p className={errorClassName}>{errorMessage}</p>}
        <Button
          variant={null}
          size={null}
          onClick={onConnect}
          disabled={isConnecting}
          className={ctaClassName}
        >
          {isConnecting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Connecting...
            </>
          ) : ctaIcon ? (
            <>
              {ctaIcon}
              {ctaLabel}
            </>
          ) : (
            ctaLabel
          )}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
